import { useAuth0 } from '@auth0/auth0-react'
import classNames from 'classnames'
import { Link, navigate } from 'gatsby'
import React, { useState, useRef, useContext, useEffect } from 'react'

import useFinnishLocaleHint from '../../hooks/useFinnishLocaleHint'
import { useQueryParams } from '../../hooks/useQueryParams'
import useTopNavHeight from '../../hooks/useTopNavHeight'
import { LocaleContext, localeValues, MeasurLocale } from '../../locale/i18n'
import { ISlugLink, staticPagePaths } from '../../locale/pathing'
import { navLinks } from '../../logic/navLinks'
import { StaticPage } from '../../logic/staticPage'
import useOnClickOutside from '../../logic/useOnClickOutside'
import Button from '../Button'
import IconCircledClear from '../icons/IconCircledClear'
import IconClose from '../icons/IconClose'
import IconMenu from '../icons/IconMenu'
import IconUser from '../icons/IconUser'
import MeasurLogo from '../MeasurLogo'

import { LocaleSelectorDesktop, LocaleSelectorMobile } from './LocaleSelector'
import LoggedInMenu from './LoggedInMenu'
import ShoppingCart from './ShoppingCart'
import SiteSearch from './SiteSearch'

import './TopNav.scss'

const NavBar = ({
  isMobileMenuOpen,
  localisedSlugs,
  setIsMobileMenuOpen,
  whitelistedLocales,
}: {
  isMobileMenuOpen: boolean
  localisedSlugs: ISlugLink[]
  setIsMobileMenuOpen: (isOpen: boolean) => void
  whitelistedLocales?: MeasurLocale[]
}) => {
  const [isSearchVisible, setIsSearchVisible] = useState(false)
  const { locale, t } = useContext(LocaleContext)
  const { appendSearch } = useQueryParams()

  const { showFinnishLocaleHint, storeShowFinnishLocaleHint } =
    useFinnishLocaleHint({
      localisedSlugs,
      showLocaleHint:
        !whitelistedLocales ||
        whitelistedLocales.length === localeValues.length,
    })

  const finnishSlug = localisedSlugs.find(
    alt => alt.locale === MeasurLocale.FINLAND_FINNISH,
  )

  // For useOutsideClick
  const ref = useRef<HTMLDivElement>(null)

  const { isAuthenticated } = useAuth0()

  useOnClickOutside(ref, () => {
    setIsMobileMenuOpen(false)
  })

  return (
    <div className="navbar__nav gutter gutter--center" ref={ref}>
      <Link to={staticPagePaths[StaticPage.INDEX][locale]}>
        <MeasurLogo isCollapsible />
      </Link>

      <SiteSearch
        setIsVisible={v => setIsSearchVisible(v)}
        isVisible={isSearchVisible}
      />

      <div
        className={classNames('navbar__links', {
          'navbar__links--hide': isSearchVisible,
        })}
      >
        {navLinks.map(page => (
          <Link
            key={page}
            to={staticPagePaths[page][locale]}
            className="navbar__link"
            activeClassName="active"
            partiallyActive
          >
            {t(`nav.${page}`)}
          </Link>
        ))}
        <LocaleSelectorMobile
          localisedSlugs={localisedSlugs}
          storeShowLocaleHint={storeShowFinnishLocaleHint}
        />
      </div>
      <ShoppingCart />
      <div className="navbar__separator" />
      {!isAuthenticated && (
        <a
          className="navbar__loginLink"
          href={staticPagePaths[StaticPage.PORTAL][locale]}
          aria-label={t('portal.loginButton')}
          data-test-id="login-button"
        >
          <IconUser />
          <span>{t('portal.loginButtonShort')}</span>
        </a>
      )}
      <LocaleSelectorDesktop
        localisedSlugs={localisedSlugs}
        storeShowLocaleHint={storeShowFinnishLocaleHint}
      >
        {showFinnishLocaleHint && finnishSlug && (
          <div className="navbar__popup navbar__localeHintPopup">
            <p>
              Moi! Tietokoneesi asetusten mukaan puhut myös suomea. Haluaisitko
              lukea tämän sivun suomeksi?
            </p>
            <div className="navbar__localeHintButtons">
              <Button
                onClick={() => {
                  storeShowFinnishLocaleHint(false)
                  void navigate(appendSearch(finnishSlug.slug))
                }}
              >
                Kyllä
              </Button>
              <Button
                variant="outline"
                onClick={() => storeShowFinnishLocaleHint(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </LocaleSelectorDesktop>

      <Button
        aria-label="Menu"
        className="navbar__icon nav:hidden!"
        onClick={event => {
          event.stopPropagation()
          setIsMobileMenuOpen(!isMobileMenuOpen)
        }}
        variant="plain"
      >
        {isMobileMenuOpen ? <IconClose /> : <IconMenu />}
      </Button>
    </div>
  )
}

const TopNav = ({
  localisedSlugs,
  whitelistedLocales,
}: {
  localisedSlugs: ISlugLink[]
  whitelistedLocales?: MeasurLocale[]
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { appendSearch } = useQueryParams()

  const { showFinnishLocaleHint, storeShowFinnishLocaleHint } =
    useFinnishLocaleHint({
      localisedSlugs,
      showLocaleHint:
        !whitelistedLocales ||
        whitelistedLocales.length === localeValues.length,
    })

  const finnishLocale = localisedSlugs.find(
    alt => alt.locale === MeasurLocale.FINLAND_FINNISH,
  )

  const heightStore = useTopNavHeight()
  const ref = useRef<HTMLDivElement>(null)
  const { isAuthenticated } = useAuth0()

  useEffect(() => {
    if (ref.current) {
      heightStore.setHeight(ref.current.getBoundingClientRect().height)
    }
  }, [isAuthenticated])

  return (
    <nav className="topNav">
      <div
        className={classNames('topNav__stickyContent', {
          'topNav__stickyContent--open': isMobileMenuOpen,
        })}
        ref={ref}
      >
        <NavBar
          isMobileMenuOpen={isMobileMenuOpen}
          localisedSlugs={localisedSlugs}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          whitelistedLocales={whitelistedLocales}
        />
        {isAuthenticated && <LoggedInMenu />}
      </div>
      <div
        className="topNav__pusher"
        style={{ marginTop: heightStore.height }}
      />
      {!isAuthenticated && showFinnishLocaleHint && finnishLocale && (
        <div className="navbar__localeHintNote gutter">
          Vaihdetaanko kieltä?
          <Link
            className="navbar__localeHintItem"
            data-test-id="mobile-locale-hint-link"
            to={appendSearch(finnishLocale.slug)}
            onClick={() => storeShowFinnishLocaleHint(false)}
          >
            {' '}
            Sama sivu suomeksi
          </Link>
          <div
            className="navbar__localeHintItem"
            onClick={() => storeShowFinnishLocaleHint(false)}
          >
            <IconCircledClear fill="black" stroke="white" />
          </div>
        </div>
      )}
    </nav>
  )
}

export default TopNav
